import { useState } from 'react'
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import {
  CHANGE_KIND_LABELS,
  RELEASES,
  groupChanges,
  groupReleaseLines,
  releasesNewestFirst,
  type ChangeKind,
  type Release,
} from '@/lib/changelog'

const KIND_STYLE: Record<ChangeKind, string> = {
  added: 'bg-emerald-100 text-emerald-800',
  improved: 'bg-blue-100 text-blue-800',
  fixed: 'bg-amber-100 text-amber-800',
}

function formatDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * The version the clinic is on, and what went into it.
 *
 * `releases` comes from useReleases(), which read app_releases and fell back to
 * the compiled RELEASES when the table was missing or empty. The default here
 * is that same fallback, so a badge rendered anywhere else still shows a
 * version. The version shown is the NEWEST of whatever it was given, sorted as
 * three counters, never the first element of the array.
 *
 * Anything below 1.0.0 is beta by Samuel's standing rule and says so.
 */
export function VersionBadge({ releases: given = RELEASES }: { releases?: Release[] }) {
  const [open, setOpen] = useState(false)
  const releases = releasesNewestFirst(given)
  const currentVersion = releases[0]?.version ?? '0.0.0'
  const isBeta = Number(currentVersion.split('.')[0]) === 0
  const lines = groupReleaseLines(releases)
  const currentLine = lines[0]?.line
  const [selected, setSelected] = useState(currentLine)
  const selectedLine = lines.find((group) => group.line === selected) ?? lines[0]

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (nextOpen) setSelected(currentLine)
      setOpen(nextOpen)
    }}>
      <DialogTrigger asChild>
      <button
        type="button"
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        title="See what's new"
      >
        <span className="font-mono font-semibold tabular-nums">v{currentVersion}</span>
        {isBeta && (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
            beta
          </span>
        )}
        <span aria-hidden className="text-gray-300">
          ·
        </span>
        <span className="hidden sm:inline">What&rsquo;s new</span>
      </button>

      </DialogTrigger>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-h-[80vh] sm:p-0">
            <div className="border-b border-gray-200 px-5 py-4 pr-12">
              <DialogTitle className="text-base font-semibold text-gray-900">What&rsquo;s new</DialogTitle>
              <DialogDescription className="mt-0.5 text-xs text-gray-400">Every update to the system, newest first.</DialogDescription>
            </div>

            <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
              {/* Version list. Only shown once there is more than one release. */}
              {lines.length > 0 && (
                <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-gray-200 px-4 py-3 sm:w-52 sm:flex-col sm:overflow-y-auto sm:border-b-0 sm:border-r sm:py-4">
                  {lines.map((group) => {
                    const active = group.line === selectedLine?.line
                    return (
                      <button
                        key={group.line}
                        type="button"
                        onClick={() => setSelected(group.line)}
                        aria-pressed={active}
                        className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors sm:whitespace-normal ${
                          active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span className="block font-mono font-semibold tabular-nums">v{group.line}{group.line === currentLine ? " · Current" : ""}</span>
                        <span className="block text-xs text-gray-400">{group.releases.length} {group.releases.length === 1 ? "release" : "releases"}</span>
                      </button>
                    )
                  })}
                </div>
              )}

              {selectedLine && (
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                  {selectedLine.releases.map((release) => (
                  <article key={release.version} className="mb-6 border-b border-gray-100 pb-6 last:mb-0 last:border-0">
                  <div className="mb-4">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="font-mono text-lg font-semibold tabular-nums text-gray-900">
                        v{release.version}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(release.date)}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{release.headline}</p>
                  </div>

                  <div className="flex flex-col gap-5">
                    {groupChanges(release).map((group) => (
                      <section key={group.kind}>
                        <span
                          className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${KIND_STYLE[group.kind]}`}
                        >
                          {CHANGE_KIND_LABELS[group.kind]}
                        </span>
                        <ul className="mt-2 flex flex-col gap-2">
                          {group.items.map((text, i) => (
                            <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-gray-700">
                              <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
                              <span>{text}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                  </article>
                  ))}
                </div>
              )}
            </div>
      </DialogContent>
    </Dialog>
  )
}

export default VersionBadge
